import {
  Users,
  FileText,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Shield,
  Plus,
  FolderPlus,
  Calculator,
  AlertCircle,
} from "lucide-react";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSinistros } from "@/hooks/useSinistros";
import { useClientesCount } from "@/hooks/useClientes";
import { useProjetosCount, useTotalPaineisDashboard } from "@/hooks/useProjetos";
import { usePedidosCount } from "@/hooks/usePedidos";
import { useFinanceiroStats } from "@/hooks/useFinanceiroStats";
import { useDashboardVolumeMensal } from "@/hooks/useDashboardVolumeMensal";
import { useAuth } from "@/hooks/useAuth";
import { calcularDiasAteVencimento, useRenovacoesSeguroDashboard } from "@/hooks/useAgenda";
import { cn } from "@/lib/utils";

function goTo(section: string) {
  window.dispatchEvent(new CustomEvent("mudar_secao", { detail: section }));
}

const COR_CLIENTES = "#C9C9C9"; // cinza
const COR_PROJETOS = "#9CA3AF"; // cinza médio
const COR_PEDIDOS = "#4B5563"; // cinza escuro
const COR_PAINEIS = "#111827"; // preto

/** Dashboard Fynix com paleta SunPro (cinza #C9C9C9). */
export function DashboardFynix() {
  const { canAccessContasAPagar } = useAuth();
  const { data: sinistros = [] } = useSinistros();
  const { data: clientesCount = 0 } = useClientesCount();
  const { data: projetosCount = 0 } = useProjetosCount();
  const { data: pedidosCount = 0 } = usePedidosCount();
  const { data: totalPaineis = 0 } = useTotalPaineisDashboard();
  const { data: renovacoesDashboard } = useRenovacoesSeguroDashboard(5);
  const { data: volumeMensal } = useDashboardVolumeMensal();
  const financeiroStats = useFinanceiroStats();

  const chartSeries = volumeMensal?.series ?? [];
  const chartYear = volumeMensal?.year ?? new Date().getFullYear();
  const chartTemDados = chartSeries.some(
    (m) => m.clientes > 0 || m.projetos > 0 || m.pedidos > 0 || m.paineis > 0
  );

  const stats = useMemo(
    () => [
      { title: "Clientes Cadastrados", value: clientesCount.toString(), icon: Users, highlight: false },
      { title: "Projetos Criados", value: projetosCount.toString(), icon: Shield, highlight: false },
      { title: "Pedidos Ativos", value: pedidosCount.toString(), icon: FileText, highlight: true },
      { title: "Total Painéis", value: totalPaineis.toString(), icon: TrendingUp, highlight: false },
    ],
    [clientesCount, projetosCount, pedidosCount, totalPaineis]
  );

  const receitaTotal = useMemo(
    () =>
      sinistros
        .filter((s) => s.status === "finalizado" && s.valor_aprovado)
        .reduce((sum, s) => sum + (s.valor_aprovado || 0), 0),
    [sinistros]
  );

  const sinistrosRecentes = useMemo(
    () =>
      [...sinistros]
        .sort(
          (a, b) =>
            new Date(b.created_at || "").getTime() - new Date(a.created_at || "").getTime()
        )
        .slice(0, 5),
    [sinistros]
  );

  const renovacoes = renovacoesDashboard?.data ?? [];

  const sinistrosAbertos = sinistros.filter((s) => s.status === "aberto").length;
  const sinistrosAnalise = sinistros.filter((s) => s.status === "analise").length;
  const sinistrosFinalizados = sinistros.filter((s) => s.status === "finalizado").length;

  const acoes = [
    { label: "Novo Cliente", desc: "Cadastrar cliente", section: "clientes", icon: Plus },
    { label: "Novo Projeto", desc: "Criar projeto", section: "projetos", icon: FolderPlus },
    { label: "Novo Orçamento", desc: "Criar orçamento", section: "orcamentos", icon: Calculator },
    { label: "Novo Sinistro", desc: "Registrar sinistro", section: "sinistros", icon: AlertCircle },
  ];

  const statusSinistroBadge = (status?: string) => {
    const s = (status || "").toLowerCase();
    if (s === "finalizado" || s === "aprovado") return "bg-white text-fynix-ink border-fynix-line";
    if (s === "analise") return "bg-white text-amber-800 border-amber-200";
    if (s === "aberto") return "bg-white text-fynix-ink-soft border-fynix-line";
    if (s === "cancelado" || s === "rejeitado") return "bg-white text-red-700 border-red-200";
    return "bg-white text-fynix-muted border-fynix-line";
  };

  return (
    <div className="crm-fynix -m-6 min-h-[calc(100vh-5.5rem)] bg-fynix-surface-muted p-4 md:p-5">
      <header className="mb-3">
        <h1 className="crm-title text-xl text-fynix-ink">Dashboard</h1>
        <p className="text-xs text-fynix-muted">Visão geral do sistema SUNPRO</p>
      </header>

      <div className="space-y-3">
        {/* Gráfico + Indicadores */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 items-stretch">
          <div className="xl:col-span-8 rounded-2xl bg-white border border-fynix-line p-3.5 shadow-sm flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div>
                <p className="text-[11px] text-fynix-muted">Criados por mês — {chartYear}</p>
                <p className="text-[10px] text-fynix-muted">
                  Indicadores ao lado = totais gerais (todos os anos)
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fynix-muted">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: COR_CLIENTES }} />
                  Clientes
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: COR_PROJETOS }} />
                  Projetos
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: COR_PEDIDOS }} />
                  Pedidos
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: COR_PAINEIS }} />
                  Painéis
                </span>
              </div>
            </div>

            {chartTemDados ? (
              <div className="h-[200px] mt-1">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartSeries} barGap={2} barCategoryGap="12%">
                    <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="mes"
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      allowDecimals={false}
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      allowDecimals={false}
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      cursor={{ fill: "#F3F4F6", opacity: 0.8 }}
                      contentStyle={{
                        borderRadius: 10,
                        borderColor: "#E5E7EB",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                        fontSize: 12,
                      }}
                      labelStyle={{ color: "#6B7280", fontSize: 11 }}
                      formatter={(value: number, name: string) => {
                        const labels: Record<string, string> = {
                          clientes: "Clientes",
                          projetos: "Projetos",
                          pedidos: "Pedidos",
                          paineis: "Painéis",
                        };
                        return [value.toLocaleString("pt-BR"), labels[name] || name];
                      }}
                    />
                    <Bar yAxisId="left" dataKey="clientes" fill={COR_CLIENTES} radius={[3, 3, 0, 0]} maxBarSize={16} />
                    <Bar yAxisId="left" dataKey="projetos" fill={COR_PROJETOS} radius={[3, 3, 0, 0]} maxBarSize={16} />
                    <Bar yAxisId="left" dataKey="pedidos" fill={COR_PEDIDOS} radius={[3, 3, 0, 0]} maxBarSize={16} />
                    <Bar yAxisId="right" dataKey="paineis" fill={COR_PAINEIS} radius={[3, 3, 0, 0]} maxBarSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-sm text-fynix-muted">
                Sem dados de criação em {chartYear} ainda.
              </div>
            )}
          </div>

          <div className="xl:col-span-4 rounded-2xl bg-fynix-mist p-3.5">
            <p className="text-[10px] uppercase tracking-widest text-fynix-ink-soft mb-2.5 px-0.5">
              Indicadores
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              {stats.map((stat) => (
                <div
                  key={stat.title}
                  className={cn(
                    "rounded-xl p-3 shadow-sm flex flex-col justify-between min-h-[88px]",
                    stat.highlight
                      ? "bg-fynix-brand text-white"
                      : "bg-white"
                  )}
                >
                  <div className="flex items-start justify-between gap-1">
                    <p
                      className={cn(
                        "text-[10px] leading-tight",
                        stat.highlight ? "text-white/70" : "text-fynix-muted"
                      )}
                    >
                      {stat.title}
                    </p>
                    <span
                      className={cn(
                        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                        stat.highlight
                          ? "bg-white/15 text-white"
                          : "bg-fynix-surface-panel text-fynix-ink"
                      )}
                    >
                      <stat.icon className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  <p
                    className={cn(
                      "crm-title text-2xl tabular-nums mt-1 leading-none",
                      stat.highlight ? "text-white" : "text-fynix-ink"
                    )}
                  >
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ações rápidas */}
        <div className="rounded-2xl bg-fynix-mist p-3">
          <h2 className="crm-title text-base text-fynix-ink mb-2">Ações Rápidas</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {acoes.map((acao) => (
              <button
                key={acao.section}
                type="button"
                onClick={() => goTo(acao.section)}
                className="rounded-xl bg-white px-3 py-2.5 text-left shadow-sm transition-colors hover:bg-fynix-surface-panel"
              >
                <div className="mb-1.5 flex h-8 w-8 items-center justify-center rounded-full border border-fynix-line bg-fynix-surface-panel text-fynix-ink">
                  <acao.icon className="h-4 w-4" />
                </div>
                <p className="font-semibold text-sm text-fynix-ink leading-tight">{acao.label}</p>
                <p className="text-[11px] text-fynix-muted mt-0.5">{acao.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Renovações + Sinistros */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-fynix-mist p-4">
            <h2 className="crm-title text-lg text-fynix-ink mb-3 flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white text-fynix-ink">
                <Shield className="h-4 w-4" />
              </span>
              Renovações próximas
            </h2>
            {renovacoes.length > 0 ? (
              <div className="rounded-2xl bg-white divide-y divide-fynix-line overflow-hidden shadow-sm">
                {renovacoes.map((evento) => {
                  const dias = calcularDiasAteVencimento(evento.data_fim) ?? 0;
                  return (
                    <div key={evento.id} className="flex items-center justify-between px-4 py-3 gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-fynix-ink truncate">
                          {evento.contrato?.numero_contrato
                            ? `Contrato ${evento.contrato.numero_contrato}`
                            : evento.titulo}
                        </p>
                        <p className="text-xs text-fynix-muted truncate">
                          {evento.cliente?.nome ||
                            (evento.cliente_id ? `Cliente ID: ${evento.cliente_id}` : "Cliente não informado")}
                        </p>
                        <p className="text-[11px] text-fynix-muted">Vence em {dias} dias</p>
                      </div>
                      <div className="text-right shrink-0">
                        {typeof evento.contrato?.valor === "number" && (
                          <p className="text-sm font-semibold text-fynix-ink tabular-nums">
                            R$ {evento.contrato.valor.toLocaleString("pt-BR")}
                          </p>
                        )}
                        <span
                          className={cn(
                            "inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] border",
                            dias <= 7
                              ? "bg-red-50 text-red-700 border-red-200"
                              : dias <= 15
                                ? "bg-orange-50 text-orange-800 border-orange-200"
                                : dias <= 30
                                  ? "bg-fynix-surface-panel text-fynix-ink border-fynix-line"
                                  : "bg-fynix-surface-panel text-fynix-muted border-fynix-line"
                          )}
                        >
                          {dias <= 7 ? "Urgente" : dias <= 15 ? "Atenção" : dias <= 30 ? "Próximo" : "Normal"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl bg-white text-center py-10 text-fynix-muted shadow-sm">
                <Shield className="h-12 w-12 mx-auto mb-3 text-fynix-brand-light" />
                <p className="text-sm font-medium text-fynix-ink">Nenhuma renovação próxima</p>
                <p className="text-xs mt-1">Eventos da agenda aparecerão aqui</p>
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-fynix-mist p-5">
            <h2 className="crm-title text-lg text-fynix-ink mb-3 flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white text-fynix-ink">
                <AlertTriangle className="h-4 w-4" />
              </span>
              Sinistros recentes
            </h2>
            {sinistrosRecentes.length > 0 ? (
              <div className="rounded-2xl bg-white divide-y divide-fynix-line overflow-hidden shadow-sm">
                {sinistrosRecentes.map((sinistro) => (
                  <div key={sinistro.id} className="flex items-center justify-between px-4 py-3 gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-fynix-ink truncate">
                        {sinistro.cliente?.nome || `Cliente ID: ${sinistro.cliente_id}`}
                      </p>
                      <p className="text-xs text-fynix-muted flex items-center gap-1.5 flex-wrap">
                        <span>{sinistro.tipo}</span>
                        <span
                          className={cn(
                            "inline-flex px-1.5 py-0.5 rounded-full text-[10px] border capitalize",
                            statusSinistroBadge(sinistro.status)
                          )}
                        >
                          {sinistro.status}
                        </span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-fynix-ink tabular-nums">
                        {sinistro.valor_estimado
                          ? `R$ ${sinistro.valor_estimado.toLocaleString("pt-BR")}`
                          : "N/A"}
                      </p>
                      <p className="text-[11px] text-fynix-muted">
                        {sinistro.created_at
                          ? new Date(sinistro.created_at).toLocaleDateString("pt-BR")
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl bg-white text-center py-10 text-fynix-muted shadow-sm">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-fynix-brand-light" />
                <p className="text-sm font-medium text-fynix-ink">Nenhum sinistro registrado</p>
                <p className="text-xs mt-1">Os sinistros aparecerão quando forem criados</p>
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas dos sinistros */}
        <div className="rounded-3xl bg-fynix-mist p-5">
          <h2 className="crm-title text-lg text-fynix-ink mb-4 flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white text-fynix-ink">
              <TrendingUp className="h-4 w-4" />
            </span>
            Estatísticas dos Sinistros
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Abertos", sub: "Aguardando análise", value: sinistrosAbertos },
              { label: "Em Análise", sub: "Sendo avaliados", value: sinistrosAnalise },
              { label: "Finalizados", sub: "Processo concluído", value: sinistrosFinalizados },
              {
                label: "Receita Total",
                sub: "Sinistros aprovados",
                value: `R$ ${receitaTotal.toLocaleString("pt-BR")}`,
              },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl bg-white p-4 shadow-sm">
                <p className="crm-title text-2xl text-fynix-ink tabular-nums">{item.value}</p>
                <p className="text-sm font-medium text-fynix-ink mt-1">{item.label}</p>
                <p className="text-xs text-fynix-muted">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Visão financeira */}
        {canAccessContasAPagar() && (
          <div className="rounded-3xl bg-fynix-mist p-5">
            <h2 className="crm-title text-lg text-fynix-ink mb-4 flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white text-fynix-ink">
                <DollarSign className="h-4 w-4" />
              </span>
              Visão Financeira
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-fynix-muted">Pedidos Ativos</p>
                    <p className="crm-title text-2xl text-fynix-ink mt-1 tabular-nums">{pedidosCount}</p>
                    <p className="text-[11px] text-fynix-muted mt-1">Total cadastrados</p>
                  </div>
                  <FileText className="h-5 w-5 text-fynix-brand-mid" />
                </div>
              </div>
              <div className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-fynix-muted">Pagamentos Pendentes</p>
                    <p className="crm-title text-2xl text-fynix-ink mt-1 tabular-nums">
                      R${" "}
                      {financeiroStats.pagamentosPendentes.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-[11px] text-fynix-muted mt-1">
                      {financeiroStats.totalClientesPendentes} cliente
                      {financeiroStats.totalClientesPendentes !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <Calendar className="h-5 w-5 text-fynix-brand-mid" />
                </div>
              </div>
              <div className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-fynix-muted">Em Atraso</p>
                    <p className="crm-title text-2xl text-red-700 mt-1 tabular-nums">
                      R${" "}
                      {financeiroStats.pagamentosEmAtraso.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-[11px] text-red-600 mt-1">
                      {financeiroStats.totalClientesEmAtraso} cliente
                      {financeiroStats.totalClientesEmAtraso !== 1 ? "s" : ""} em atraso
                    </p>
                  </div>
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-fynix-muted">Pagos</p>
                    <p className="crm-title text-2xl text-sunservice-green-dark mt-1 tabular-nums">
                      R${" "}
                      {financeiroStats.pagamentosPagos.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-[11px] text-fynix-muted mt-1">
                      {financeiroStats.totalPagamentosRealizados} pagamento
                      {financeiroStats.totalPagamentosRealizados !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <DollarSign className="h-5 w-5 text-sunservice-green" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
